import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { api } from '../../config/api';
import { Button, Modal, Input, Table, Tooltip, Tag, Space, Card, Empty, Spin } from 'antd';
import { doc, deleteField, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Editor, Viewer } from '@toast-ui/react-editor';
import { Info, Plus, Eye, Edit, Calendar, User, Trash2 } from 'lucide-react';
import { useMyContext } from '../../context/MyContext';
import { useNavigate } from 'react-router-dom';

//  if (!user.testAccept ||(user.testVersion && user.testVersion !== testTnc.version)) 

const CreatedByName = ({ id }) => {
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getName = async () => {
            try {
                const userRef = doc(db, 'users', id);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    setName(userSnap.data().name);
                }
            } catch (error) {
                console.error('Error getting user name from firebase', error);
            } finally {
                setLoading(false);
            }
        };
        if (id) getName();
    }, [id]);

    if (loading) return <span className="text-gray-400">Loading...</span>;
    return <p>{name || 'Unknown'}</p>;
};

const TermsConditionsTable = () => {
    const { user, updateUserProfile } = useMyContext()
    const navigate = useNavigate()
    const [terms, setTerms] = useState([]);
    const [formData, setFormData] = useState({ name: '', content: '' });
    const [showModal, setShowModal] = useState(false);
    const [preview, setPreview] = useState(false);
    const [previewContent, setPreviewContent] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false); // State for delete modal visibility
    const [deleteItem, setDeleteItem] = useState(null); // State for holding item to be deleted
    const editorRef = useRef();
    const viewerRef = useRef();

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleAddTerms = async () => {
        const content = editorRef.current?.getInstance().getMarkdown();
        const updatedForm = { ...formData, content };
        setFormData(updatedForm);

        if (!updatedForm.name.trim()) {
            toast.error('Please enter a name');
            return;
        }

        if (!content.trim()) {
            toast.error('Please enter content');
            return;
        }

        setSaving(true);
        try {
            await api.post('/terms-conditions', updatedForm);
            toast.success('Terms and Conditions added successfully');
            setShowModal(false);
            setFormData({ name: '', content: '' });
            getTerms();
        } catch (error) {
            console.error('Error adding terms and conditions', error);
            toast.error('Error adding terms and conditions');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateTerms = async () => {
        const updatedContent = editorRef.current?.getInstance().getMarkdown();

        if (!formData.name.trim()) {
            toast.error('Please enter a name');
            return;
        }

        if (!updatedContent.trim()) {
            toast.error('Please enter content');
            return;
        }

        const updatedData = {
            id: editId,
            name: formData.name,
            content: updatedContent,
        };

        setSaving(true);
        try {
            await api.put(`/terms-conditions/${editId}`, updatedData);
            toast.success('Terms and Conditions updated successfully');
            setShowModal(false);
            setEditMode(false);
            setFormData({ name: '', content: '' });
            getTerms();
        } catch (error) {
            console.error('Error updating terms and conditions', error);
            toast.error('Error updating terms and conditions');
        } finally {
            setSaving(false);
        }
    };

    const handleEditClick = (record) => {
        setEditMode(true);
        setEditId(record._id);
        setFormData({ name: record.name, content: record.content });
        setShowModal(true);
    };

    const handleDeleteClick = (record) => {
        setDeleteItem(record);
        setDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!deleteItem) return;

        try {
            await api.delete(`/terms-conditions/${deleteItem._id}`);
            toast.success('Terms and Conditions deleted successfully');
            setDeleteModal(false);
            getTerms();
        } catch (error) {
            toast.error('Error deleting terms and conditions');
            console.error('Error deleting terms and conditions', error);
        }
    };

    const getTerms = async () => {
        setLoading(true);
        try {
            const res = await api.get('/terms-conditions');
            const formatted = res.data.data.map((item) => ({
                key: item._id,
                ...item,
            }));
            setTerms(formatted);
        } catch (error) {
            console.error('Error fetching terms and conditions');
            toast.error('Error fetching terms and conditions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getTerms();
    }, []);

    // Ensure the preview viewer always shows the latest selected content
    useEffect(() => {
        if (preview && viewerRef.current && typeof previewContent === 'string') {
            try {
                viewerRef.current.getInstance().setMarkdown(previewContent || '');
            } catch (e) {
                // Fallback: ignore if viewer not ready
            }
        }
    }, [preview, previewContent]);

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: '30%',
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: '25%',
            render: (createdBy) => <CreatedByName id={createdBy} />,
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: '25%',
            render: (createdAt) => formatDate(createdAt),
        },
        {
            title: 'Last Updated At',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: '25%',
            render: (updatedAt) => formatDate(updatedAt),
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            width: '20%',
            render: (_, row) => (
                <Space size="small">
                    <button
                        type="button"
                        className="bg-cus-lblue rounded px-3 py-1 text-white font-medium flex items-center gap-1"
                        onClick={() => {
                            setPreviewContent(row.content);
                            setPreview(true);
                        }}
                    >
                        <Eye strokeWidth={1.5} size={20} />
                    </button>
                    <button
                        type="button"
                        className="bg-cus-green rounded px-2 py-1 text-white font-medium flex items-center gap-1"
                        onClick={() => handleEditClick(row)}
                    >
                        <Edit strokeWidth={1.5} size={20} />
                    </button>
                    {/* <button
                        type="button"
                        className="bg-cus-red rounded px-2 py-1 text-white font-medium flex items-center gap-1"
                        onClick={() => handleDeleteClick(row)}
                    >
                        <Trash2 strokeWidth={1.5} size={20} />
                    </button> */}
                </Space>
            ),
        },
    ];
    return (
        <div className="w-full h-full">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-1">
                        Terms and Conditions
                    </h2>
                    <p className="text-gray-500">Manage your terms and conditions here</p>
                </div>
                {/* <Button
                    type="primary"
                    size="large"
                    icon={<Plus size={18} />}
                    onClick={() => {
                        setEditMode(false);
                        setFormData({ name: '', content: '' });
                        setShowModal(true);
                    }}
                    className="flex items-center gap-2"
                >
                    Add New
                </Button> */}
            </div>

            <Table
                columns={columns}
                dataSource={terms}
                loading={loading}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                }}
                locale={{
                    emptyText: (
                        <Empty
                            description="No terms and conditions found"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                            <Button
                                type="primary"
                                icon={<Plus size={16} />}
                                onClick={() => {
                                    setEditMode(false);
                                    setFormData({ name: '', content: '' });
                                    setShowModal(true);
                                }}
                            >
                                Create First Entry
                            </Button>
                        </Empty>
                    ),
                }}
            />

            {/* Add/Edit Modal */}
            <Modal
                title={
                    <div className="flex items-center gap-2 text-xl">
                        {editMode ? (
                            <>
                                <Edit size={20} />
                                <span>Edit Terms and Conditions</span>
                            </>
                        ) : (
                            <>
                                <Plus size={20} />
                                <span>Add Terms and Conditions</span>
                            </>
                        )}
                    </div>
                }
                open={showModal}
                centered
                width={1000}
                onCancel={() => {
                    setShowModal(false);
                    setEditMode(false);
                    setFormData({ name: '', content: '' });
                }}
                footer={[
                    <Button
                        key="cancel"
                        size="large"
                        onClick={() => {
                            setShowModal(false);
                            setEditMode(false);
                            setFormData({ name: '', content: '' });
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="ok"
                        type="primary"
                        size="large"
                        loading={saving}
                        onClick={editMode ? handleUpdateTerms : handleAddTerms}
                    >
                        {saving ? 'Saving...' : editMode ? 'Update' : 'Save'}
                    </Button>,
                ]}
                destroyOnClose
            >
                <div className="space-y-5 pt-4">
                    <div>
                        <label className="block font-semibold text-gray-700 mb-2">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            placeholder="Enter a descriptive name"
                            size="large"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>

                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <label className="font-semibold text-gray-700">
                                Content <span className="text-red-500">*</span>
                            </label>
                            <Tooltip title="Use the editor below to add or edit terms and conditions. You can format text, add lists, and structure your content.">
                                <Info size={16} className="text-gray-400 cursor-help" />
                            </Tooltip>
                        </div>
                        <div className="border rounded-lg overflow-hidden">
                            <Editor
                                ref={editorRef}
                                previewStyle="vertical"
                                height="450px"
                                initialValue={formData.content || ''}
                                initialEditType="wysiwyg"
                                usageStatistics={false}
                                key={editMode ? formData.content : 'new'}
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Preview Modal */}
            <Modal
                width={1000}
                title={
                    <div className="flex items-center gap-2 text-xl">
                        <Eye size={20} />
                        <span>Preview</span>
                    </div>
                }
                open={preview}
                onCancel={() => setPreview(false)}
                styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
                footer={[
                    <Button key="close" type="primary" size="large" onClick={() => setPreview(false)}>
                        Close
                    </Button>,
                ]}
                centered
            >
                <div className="pt-4 prose max-w-none">
                    <Viewer ref={viewerRef} initialValue={previewContent || 'No content to preview'} />
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={deleteModal}
                title={`Delete ${deleteItem?.name}?`}
                onCancel={() => { setDeleteModal(false); setDeleteItem(null); }}
                footer={[
                    <Button key="cancel" size="large" onClick={() => { setDeleteModal(false); setDeleteItem(null); }}>
                        Cancel
                    </Button>,
                    <Button key="delete" danger type="primary" size="large" onClick={handleDelete}>
                        Delete
                    </Button>,
                ]}
                centered
            >
                <p>Are you sure you want to delete this term and condition?</p>
            </Modal>
        </div>
    );
};

export default TermsConditionsTable;
